import React from "react";
import { cockpit } from "@/api";
import type { IncomingWorkCounts } from "@/api/types";
import { useLiveInvalidation, useLiveScopeKey } from "@/live/live-provider";
import { useT } from "../i18n";

export type IncomingWorkRead =
  | { status: "ready"; counts: IncomingWorkCounts }
  | { status: "loading" | "unavailable" };

/** Validate only the aggregate's shape; the server owns eligibility and currentness. */
export function incomingWorkRead(counts: IncomingWorkCounts | undefined): IncomingWorkRead {
  return counts && [counts.processing, counts.unseen_ready].every((n) => Number.isSafeInteger(n) && n >= 0)
    ? { status: "ready", counts }
    : { status: "unavailable" };
}

/**
 * #2259: the business-wide counts for a page with no worklist of its own (Filed). The Overview
 * reads them from its own summary instead. The same limit=1 summary asks for a number, not a list;
 * a refresh keeps the last answer on screen rather than blinking back to "loading".
 */
export function useIncomingWorkRead(): IncomingWorkRead {
  const scopeKey = useLiveScopeKey();
  const invalidations = useLiveInvalidation("requests", "workspace", "customers", "pipeline");
  const [state, setState] = React.useState<{ scope: string; read: IncomingWorkRead } | null>(null);

  // A tab brought back to the front needs no read of its own: a stream that went quiet meanwhile
  // is reopened, and its opening event moves these domains (#2274).
  React.useEffect(() => {
    let current = true;
    cockpit.listConversations({ group_by: "request", limit: 1, offset: 0 }).then(
      (page) => { if (current) setState({ scope: scopeKey, read: incomingWorkRead(page.counts?.incoming_work) }); },
      () => { if (current) setState({ scope: scopeKey, read: { status: "unavailable" } }); },
    );
    return () => { current = false; };
  }, [scopeKey, invalidations]);

  return state?.scope === scopeKey ? state.read : { status: "loading" };
}

export function OverviewIncomingWork({ read }: { read: IncomingWorkRead }) {
  const t = useT();
  return (
    <div className="ov-incoming" role="status" aria-live="polite" aria-atomic="true">
      {read.status === "ready" ? (
        (["processing", "unseen_ready"] as const).map((kind) => {
          const count = read.counts[kind];
          return (
            <span key={kind} className={`ov-incoming__item ov-incoming__item--${kind}`}>
              <span className="sr-only">{t(`overview.incoming.${kind}.count`, { n: count })}</span>
              <span className="ov-incoming__visual" aria-hidden="true">
                <span className={`ov-incoming__lamp${count > 0 ? " ov-incoming__lamp--on" : ""}`} />
                <span>{t(`overview.incoming.${kind}`)}</span>
                {count >= 2 && <span className="ov-incoming__count">{count}</span>}
              </span>
            </span>
          );
        })
      ) : <span className="ov-incoming__unknown">{t(`overview.incoming.${read.status}`)}</span>}
    </div>
  );
}
